import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Badge } from 'antd'
import {
  HomeOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  HistoryOutlined,
  UserOutlined,
  CrownOutlined
} from '@ant-design/icons'
import { useAuthStore } from '@/store/authStore'
import { motion } from 'framer-motion'

const MobileBottomNav: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAuthenticated } = useAuthStore()

  const navItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: '首页',
      path: '/'
    },
    {
      key: '/video',
      icon: <VideoCameraOutlined />,
      label: '视频',
      path: '/video',
      requireAuth: true
    },
    {
      key: '/image',
      icon: <PictureOutlined />,
      label: '图像',
      path: '/image',
      requireAuth: true
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: '历史',
      path: '/history',
      requireAuth: true
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: '我的',
      path: '/profile',
      requireAuth: true
    }
  ]

  if (user?.role === 'ADMIN') {
    navItems.push({
      key: '/admin',
      icon: <CrownOutlined />,
      label: '管理',
      path: '/admin',
      requireAuth: true
    })
  }

  const handleNavClick = (path: string, requireAuth?: boolean) => {
    if (requireAuth && !isAuthenticated) {
      navigate('/login')
      return
    }
    navigate(path)
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl safe-area-bottom">
      <div
        className="grid h-16"
        style={{ gridTemplateColumns: `repeat(${Math.min(navItems.length, 5)}, minmax(0, 1fr))` }}
      >
        {navItems.slice(0, 5).map((item) => {
          const active = isActive(item.path)
          
          return (
            <motion.button
              key={item.key}
              onClick={() => handleNavClick(item.path, item.requireAuth)}
              className="relative flex flex-col items-center justify-center space-y-1 transition-all"
              whileTap={{ scale: 0.9 }}
            >
              {/* 激活指示器 */}
              {active && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-b-full"
                  initial={false}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30
                  }}
                />
              )}

              {/* 图标 */}
              <motion.div
                className={`text-2xl transition-colors ${
                  active 
                    ? 'text-blue-600' 
                    : 'text-gray-400'
                }`}
                animate={active ? {
                  scale: [1, 1.2, 1],
                  transition: { duration: 0.3 }
                } : {}}
              >
                {item.key === '/profile' && user?.pointsBalance !== undefined ? (
                  <Badge
                    count={user.pointsBalance}
                    overflowCount={999}
                    size="small"
                    offset={[5, -5]}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </motion.div>

              {/* 标签 */}
              <span 
                className={`text-xs font-medium transition-colors ${
                  active 
                    ? 'text-blue-600' 
                    : 'text-gray-500'
                }`}
              >
                {item.label}
              </span>

              {/* 背景高亮 */}
              {active && (
                <motion.div
                  className="absolute inset-0 bg-blue-50 rounded-t-2xl -z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}

export default MobileBottomNav

